/**
 * Set Product Quantity Action
 * Triggered by: arc:action:quantitySet
 */

export const runQuantitySet = async (data = {}) => {
    try {
        const { 
            quantity, 
            minQuantity = 1,
            maxQuantity = null,
            targetSelector = "[data-prodify-quantity-presentation]",
            autoUpdatePrice = true,
            validateRange = true 
        } = data;

        if (typeof quantity !== 'number' && !quantity) {
            throw new Error('Quantity is required and must be a number');
        }

        const quantityInput = document.querySelector(targetSelector);
        
        if (!quantityInput) {
            throw new Error(`Quantity input not found with selector: ${targetSelector}`);
        }

        const currentValue = parseInt(quantityInput.value) || 0;
        let newValue = parseInt(quantity);

        // Validate quantity range if enabled
        if (validateRange) {
            if (newValue < minQuantity) {
                newValue = minQuantity;
                
                window.dispatchEvent(new CustomEvent('quantity:minReached', {
                    detail: {
                        minQuantity,
                        attemptedValue: quantity
                    }
                }));
            }

            if (maxQuantity && newValue > maxQuantity) {
                newValue = maxQuantity;
                
                window.dispatchEvent(new CustomEvent('quantity:maxReached', {
                    detail: {
                        maxQuantity,
                        attemptedValue: quantity
                    }
                }));
            }
        }

        // Update the display input
        quantityInput.value = newValue;

        // Update form inputs and pricing
        if (autoUpdatePrice) {
            await updateFormInputValue(newValue);
        }

        // Dispatch quantity changed event
        window.dispatchEvent(new CustomEvent('quantity:changed', {
            detail: {
                oldValue: currentValue,
                newValue: newValue,
                action: 'set',
                requestedValue: quantity
            }
        }));

        return {
            success: true,
            oldValue: currentValue,
            newValue: newValue,
            requestedValue: quantity,
            message: 'Quantity set successfully'
        };

    } catch (error) {
        console.error('Quantity Set error:', error);
        
        window.dispatchEvent(new CustomEvent('quantity:error', {
            detail: {
                error: error.message,
                action: 'set'
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Helper function to update form inputs and pricing
const updateFormInputValue = async (quantity) => {
    // Update all product form quantity inputs
    const productForms = document.querySelectorAll(".pdp-details .product-form input[name='quantity']");
    
    productForms.forEach((productFormInput) => {
        productFormInput.value = quantity;
    });

    // Update add to cart button with new pricing
    const currencySymbol = window.Alpine?.store('currency')?.getCurrencySymbol() || '$';
    
    // Get base price from global state or calculate from current variant
    let basePrice = window.selectedBasePrice || 0;
    
    if (!basePrice) {
        // Try to get price from current variant
        const currentVariant = window.currentVariant;
        if (currentVariant) {
            const variantElement = document.querySelector(`input[data-variant-id="${currentVariant}"]`);
            const variantPrice = variantElement?.getAttribute("data-variant-price");
            if (variantPrice) {
                basePrice = parseFloat(variantPrice.replace("$", ""));
            }
        }
    }

    if (basePrice > 0) {
        const totalPrice = basePrice * quantity;
        
        // Dispatch event to update add button
        window.dispatchEvent(new CustomEvent('addButton:update', {
            detail: {
                disabled: false,
                text: `Add to Cart | ${currencySymbol}${totalPrice.toFixed(2)}`,
                modifyClass: false,
                price: basePrice
            }
        }));
    }
};

// Helper function to get current quantity
export const getCurrentQuantity = (targetSelector = "[data-prodify-quantity-presentation]") => {
    const quantityInput = document.querySelector(targetSelector);
    return quantityInput ? parseInt(quantityInput.value) || 0 : 0;
};

// Initialize quantity change listeners for input fields
export const initializeQuantityInputListeners = (targetSelector = "[data-prodify-quantity-presentation]") => {
    const quantityInput = document.querySelector(targetSelector);
    
    if (quantityInput) {
        // Remove existing listener to prevent duplicates
        quantityInput.removeEventListener('change', handleQuantityInputChange);
        quantityInput.removeEventListener('input', handleQuantityInputChange);
        
        // Add new listeners
        quantityInput.addEventListener('change', handleQuantityInputChange);
        quantityInput.addEventListener('input', handleQuantityInputChange);
    }
};

// Handle direct input changes
const handleQuantityInputChange = (event) => {
    const newQuantity = parseInt(event.target.value) || 1;
    
    // Trigger quantity set action
    window.dispatchEvent(new CustomEvent('arc:action:quantitySet', {
        detail: {
            quantity: newQuantity,
            autoUpdatePrice: true,
            validateRange: true
        }
    }));
};

export default runQuantitySet;
